// src/components/layout/Footer.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import {
  MessageSquare,
  Mail,
  Facebook,
  Linkedin,
  Heart,
  ExternalLink
} from 'lucide-react';
import { FaYoutube } from 'react-icons/fa';
import ShareButton from '../shared/ShareButton';
import { DDD_PRIVACY_URL, DDD_TERMS_URL, DDD_COOKIES_URL } from '../../config/legalLinks';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const dddApps = [
    {
      name: 'Deeper Dialogues',
      href: 'https://deeperdialogues.divedeepdialogues.com',
      description: 'Our flagship relationship app'
    },
    {
      name: 'The Art of Conversation',
      href: 'https://artofconversation.divedeepdialogues.com',
      description: 'Master meaningful dialogue'
    },
    {
      name: 'Inner Voice Mastery',
      href: 'https://innervoice.divedeepdialogues.com',
      description: 'Transform your self-talk'
    },
    {
      name: 'Dive Deep Workplace',
      href: 'https://divedeepworkplace.com',
      description: 'Better conversations at work'
    }
  ];

  const quickLinks = [
    { name: 'Assessment', href: '/assessment' },
    { name: 'Learning Modules', href: '/modules' },
    { name: 'Toolkit', href: '/toolkit' },
    { name: 'About DDD', href: 'https://divedeepDialogues.com', external: true },
    { name: 'Data & Privacy Settings', href: '/privacy' },
    // Legal policies are DDD-wide (src/config/legalLinks.js)
    { name: 'Privacy Policy', href: DDD_PRIVACY_URL, external: true },
    { name: 'Terms of Use', href: DDD_TERMS_URL, external: true },
    { name: 'Cookie Policy', href: DDD_COOKIES_URL, external: true },
    { name: 'Help & Support', href: 'mailto:help@divedeepdialogues.com', external: true }
  ];

  const socialLinks = [
    { name: 'Facebook', icon: Facebook, href: 'https://www.facebook.com/profile.php?id=61565456445914' },
    { name: 'YouTube', icon: FaYoutube, href: 'https://youtube.com/@DiveDeepDialogues' },
    { name: 'LinkedIn', icon: Linkedin, href: 'https://linkedin.com/company/dive-deep-dialogues' }
  ];

  return (
    <footer className="relative mt-20 border-t border-slate-800">
      <div className="container mx-auto px-4 py-10">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Brand Column */}
          <div>
            <Link to="/" className="flex items-center gap-3 mb-4 group">
              <img
                src="/art-of-story-logo.svg"
                alt="The Art of Story"
                className="w-10 h-10 group-hover:scale-110 transition-transform"
              />
              <div className="flex flex-col">
                <h3 className="text-white font-bold group-hover:text-amber-300 transition-colors" style={{ lineHeight: '1.2' }}>Art of Story</h3>
                <p className="text-xs text-amber-300 group-hover:text-amber-200 transition-colors" style={{ lineHeight: '1', marginTop: '2px' }}>by Dive Deep Dialogues</p>
              </div>
            </Link>
            <p className="text-sm text-slate-400 mb-4">
              Master the art of storytelling, from scattered anecdotes to magnetic narratives that stick and connect.
            </p>

            {/* Social Links */}
            <div className="flex gap-2">
              {socialLinks.map(social => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 bg-white/10 hover:bg-amber-600/20 rounded-lg flex items-center justify-center transition-colors"
                    aria-label={social.name}
                  >
                    <Icon className="w-4 h-4 text-slate-400 hover:text-amber-400" aria-hidden="true" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Other DDD Apps */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm">Explore Our Apps</h4>
            <ul className="space-y-3">
              {dddApps.map(app => (
                <li key={app.name}>
                  <a
                    href={app.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block"
                  >
                    <div className="flex items-start gap-1">
                      <span className="text-sm text-white group-hover:text-amber-300 transition-colors font-medium">
                        {app.name}
                      </span>
                      <ExternalLink className="w-3 h-3 text-slate-500 mt-0.5" aria-hidden="true" />
                    </div>
                    <p className="text-xs text-slate-500 group-hover:text-slate-400 transition-colors" style={{ marginTop: '2px' }}>
                      {app.description}
                    </p>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map(link => (
                <li key={link.name}>
                  {link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-400 hover:text-amber-400 text-sm transition-colors inline-flex items-center gap-1"
                    >
                      {link.name}
                      <ExternalLink className="w-3 h-3" aria-hidden="true" />
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      className="text-slate-400 hover:text-amber-400 text-sm transition-colors"
                    >
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Connect */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm">Stay Connected</h4>
            {/* This was a "weekly tips" form with no handler: nothing was ever
                sent, and pressing Subscribe reloaded the page with ?email= in
                the address, which analytics then recorded. */}
            <p className="text-sm text-slate-400">
              Questions or feedback? Email{' '}
              <a
                href="mailto:help@divedeepdialogues.com"
                className="text-amber-300 hover:text-amber-200 underline underline-offset-2"
              >
                help@divedeepdialogues.com
              </a>
            </p>
          </div>
        </div>

        {/* The in-app terms held the only "not therapy" wording, so it lives here now. */}
        <p className="text-xs text-slate-400 text-center md:text-left mb-4">
          The Art of Story is storytelling skills training, not therapy, counselling or crisis support.
          If you or someone else is in danger, call your local emergency number (000 in Australia, 111
          in New Zealand, 112 in India, 999 or 112 in the UK and Ireland, 911 in Canada and the US).
        </p>

        {/* Bottom Bar */}
        <div className="pt-6 border-t border-slate-800">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-slate-500">
              © {currentYear} The Art of Story by SILVER WOLF Productions
            </p>

            <p className="text-xs text-slate-500 flex items-center gap-1">
              Made with <Heart className="w-3 h-3 text-red-400" aria-hidden="true" /> in Australia
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
